/* eslint-disable react-hooks/exhaustive-deps,max-lines */
import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'

import { getBrands, getGenerations, getModels, isSingleElementArray } from '../../../../../common/operations'
import { TanksSizeField } from '../tank-size-field/tank-size-field'
import { GenerationChoose } from './generation-choose'
import { GenerationChooseItem } from './generation-choose-item'
import { collectFuelTypeGroups } from './generations-mapper'
import {
    brandsOptionsMapper,
    modelsOptionsMapper,
    generationOptionsMapper,
    fuelTypeOptionsMapper,
    getSelectedFuelType
} from './options-mappers'

import styles from './select-vehicle.module.css'

const FIELD_TYPE = Object.freeze({ INPUT: 'input', SELECT: 'select' })

const SelectVehicle = ({
    activationCode,
    addErrors,
    brandIdState,
    brands,
    customerNumber,
    errorTankSize,
    fuelTypeIndexState,
    fuelTypesGroupState,
    generationNoState,
    generations,
    intl,
    modelIdState,
    models,
    setBrandId,
    setBrands,
    setFuelTypeIndex,
    setFuelTypesGroup,
    setGenerationIdSelected,
    setGenerationObject,
    setGenerationSelected,
    setGenerations,
    setModelId,
    setModels,
    setTankSize,
    setTankSizeError,
    tankSize,
    // CUSTOMER PROPS DEDICATED TO DAS FLOW
    validTankSizeField,
    validatorCallback
}) => {
    const [fetchError, setFetchError] = useState(false)
    const [noDataError, setNoDataError] = useState(false)
    const [state, setStateOfProperty] = useState({
        brandId: brandIdState,
        fuelTypeIndex: fuelTypeIndexState,
        fuelTypesGroup: fuelTypesGroupState,
        generationNo: generationNoState,
        modelId: modelIdState
    })

    const { brandId, fuelTypeIndex, fuelTypesGroup, generationNo, modelId } = state

    const setState = statesArr => {
        setStateOfProperty(prevState => ({ ...prevState, ...statesArr }))
    }

    useEffect(() => {
        if (generationNo === 0) {
            setState({ brandId: 0, fuelTypeIndex: -1, fuelTypesGroup: [] })
            setBrandId(0)
            setFuelTypesGroup([])
            setFuelTypeIndex(-1)
            setModels([])
            setGenerations([])

            getBrands(activationCode, customerNumber)
                .then(response => {
                    setBrands(response.data)
                })
                .catch(() => {
                    setFetchError(true)
                    addErrors(intl.translate('ap_generic_error_text'))
                })
        }
    }, [])
    const getBrandNameById = id => {
        return brands.length !== 0 ? brands.find(br => br.brandId === id).brandName : ''
    }

    const getModelNameById = id => {
        return models.length !== 0 ? models.find(m => m.modelId === id).modelName : ''
    }

    const generationObjectBuilder = () => {
        const findGenByGenerationNo = genNo => {
            return generations[fuelTypeIndex].groupedGenerations.filter(g => g.generationNo === genNo)[0]
        }

        const brandName = getBrandNameById(brandId)
        const modelName = getModelNameById(modelId)
        const yearFrom = findGenByGenerationNo(generationNo).yearFrom
        const yearTo = findGenByGenerationNo(generationNo).yearTo.replace('null', 'ap_step3_generation_today')
        const fuelType = 'ap_step3_fueltype_' + getSelectedFuelType(generations, fuelTypeIndex).toLowerCase()

        const generationInfoObject = {
            brand: brandName,
            fuelType: fuelType,
            isFuelTypeShow: true,
            model: modelName,
            yearFrom: yearFrom,
            yearTo: yearTo
        }

        return generationInfoObject
    }

    useEffect(() => {
        validatorCallback(generationNo !== 0)

        if (generationNo !== 0) {
            setBrandId(brandId)
            setModelId(modelId)
            setFuelTypeIndex(fuelTypeIndex)
            setFuelTypesGroup(fuelTypesGroup)
            setGenerationObject(generationObjectBuilder())
        }
    }, [generationNo])

    useEffect(() => {
        if (generationNo !== 0 && fuelTypeIndex !== -1) {
            setGenerationObject(generationObjectBuilder())
        }
    }, [fuelTypeIndex])

    const setGeneration = (genNo = 0, genId = 0) => {
        setState({ generationNo: genNo })
        setGenerationSelected(genNo)
        setGenerationIdSelected(genId)
    }

    const autoFillSingleFuelTypeField = resourceData => {
        if (isSingleElementArray(collectFuelTypeGroups(resourceData))) {
            const ftGroup = collectFuelTypeGroups(resourceData)[0].groupedGenerations
            setState({
                fuelTypeIndex: 0,
                fuelTypesGroup: ftGroup
            })

            if (isSingleElementArray(ftGroup)) {
                setGeneration(ftGroup[0].generationNo, ftGroup[0].generationId)
            }
        }
    }

    const autoFillSingleModelsField = resData => {
        if (isSingleElementArray(resData)) {
            setState({ modelId: resData[0].modelId })

            getGenerations(activationCode, customerNumber, resData[0].modelId).then(genRes => {
                setGenerations(collectFuelTypeGroups(genRes.data))
                autoFillSingleFuelTypeField(genRes.data)
            })
        }
    }

    const onBrandsChange = e => {
        validatorCallback(false)

        setState({ brandId: e.target.value })
        setNoDataError(false)

        getModels(activationCode, customerNumber, e.target.value)
            .then(res => {
                setState({
                    fuelTypeIndex: -1,
                    fuelTypesGroup: [],
                    modelId: 0
                })

                setGeneration()
                setModels(res.data)
                setGenerations([])

                if (res.data.length === 0) {
                    setNoDataError(true)
                }

                autoFillSingleModelsField(res.data)
                setFetchError(false)
            })
            .catch(() => {
                setFetchError(true)
                addErrors(intl.translate('ap_generic_error_1'))
            })
    }

    const onModelsChange = e => {
        setState({ modelId: e.target.value })
        setNoDataError(false)

        getGenerations(activationCode, customerNumber, e.target.value)
            .then(res => {
                setState({
                    fuelTypeIndex: -1,
                    fuelTypesGroup: []
                })

                setGeneration()
                setGenerations(collectFuelTypeGroups(res.data))
                setState({ fuelTypesGroup: collectFuelTypeGroups(res.data) })

                if (res.data.length === 0) {
                    setNoDataError(true)
                }

                setFetchError(false)
                autoFillSingleFuelTypeField(res.data)
            })
            .catch(() => {
                setFetchError(true)
                addErrors(intl.translate('ap_generic_error_1'))
            })
    }

    const onFuelTypeChange = e => {
        const ftGroup = generations[e.target.value].groupedGenerations
        setState({
            fuelTypeIndex: e.target.value,
            fuelTypesGroup: ftGroup
        })
        setGeneration()
        setFuelTypeIndex(e.target.value)

        if (isSingleElementArray(ftGroup)) {
            setGeneration(ftGroup[0].generationNo, ftGroup[0].generationId)
        }
    }

    const getGenerationIdByGenNo = genNumber => {
        return fuelTypesGroup.find(generation => generation.generationNo === genNumber).generationId
    }

    const onGenerationsChange = e => {
        setGeneration(e.target.value, getGenerationIdByGenNo(e.target.value))
    }

    const selectFields = [
        {
            fieldType: FIELD_TYPE.SELECT,
            filtered: true,
            labelStringCode: 'ap_step4_make_label',
            mappedOptions: brandsOptionsMapper(brands),
            mobileMappedOptions: brandsOptionsMapper(brands, true),
            onChange: onBrandsChange,
            placeholderValue: 0,
            value: brandId
        },
        {
            fieldType: FIELD_TYPE.SELECT,
            filtered: true,
            labelStringCode: 'ap_step4_model_label',
            mappedOptions: modelsOptionsMapper(models),
            mobileMappedOptions: modelsOptionsMapper(models, true),
            onChange: onModelsChange,
            placeholderValue: 0,
            skipValidation: models.length === 0,
            value: modelId
        },
        {
            fieldType: FIELD_TYPE.SELECT,
            labelStringCode: 'ap_step4_fueltype_label',
            mappedOptions: fuelTypeOptionsMapper(generations, intl),
            mobileMappedOptions: fuelTypeOptionsMapper(generations, intl, true),
            onChange: onFuelTypeChange,
            placeholderValue: -1,
            skipValidation: generations.length === 0,
            value: fuelTypeIndex
        },
        {
            fieldType: FIELD_TYPE.SELECT,
            labelStringCode: 'ap_step4_year_label',
            mappedOptions: generationOptionsMapper(fuelTypesGroup, fuelTypeIndex, intl),
            mobileMappedOptions: generationOptionsMapper(fuelTypesGroup, fuelTypeIndex, intl, true),
            onChange: onGenerationsChange,
            placeholderValue: 0,
            skipValidation: fuelTypeIndex === -1,
            value: generationNo
        },
        {
            error: errorTankSize,
            fieldType: FIELD_TYPE.INPUT,
            onChange: e => setTankSize(e.target.value),
            onFocus: () => setTankSizeError(''),
            validator: validTankSizeField,
            value: tankSize
        }
    ]

    return (
        <div id="select-generation-box" className={styles['select-vehicle-container']}>
            <div id="generation-description">{intl.translate('ap_step3_generation_description')}</div>

            <GenerationChoose {...{ intl }}>
                {selectFields.map((f, index) => {
                    if (f.fieldType === FIELD_TYPE.INPUT) {
                        return (
                            <TanksSizeField
                                id={`tank-field-${index}`}
                                key={`tank-field-${index}`}
                                {...{
                                    error: f.error,
                                    intl,
                                    // eslint-disable-next-line max-lines
                                    onChange: f.onChange,
                                    onFocus: f.onFocus,
                                    validator: f.validator,
                                    value: f.value
                                }}
                            />
                        )
                    } else {
                        return (
                            <GenerationChooseItem
                                id={`gen-select-field-${index}`}
                                key={`gen-select-field-${index}`}
                                {...{
                                    filtered: f.filtered,
                                    hidden: f.hidden,
                                    label: intl.translate(f.labelStringCode),
                                    mobileMappedOptions: f.mobileMappedOptions,
                                    onChange: f.onChange,
                                    placeholderValue: f.placeholderValue,
                                    skipValidation: f.skipValidation,
                                    validationError: f.validationError,
                                    value: f.value
                                }}
                            >
                                {f.mappedOptions}
                            </GenerationChooseItem>
                        )
                    }
                })}
            </GenerationChoose>
            <div id="generation-error-message" className={styles['server-error-message']}>
                {fetchError ? intl.translate('ap_generic_error_text') : ''}
                {noDataError ? intl.translate('ap_brand_model_error') : ''}
            </div>
        </div>
    )
}

SelectVehicle.propTypes = {
    activationCode: PropTypes.string,
    addErrors: PropTypes.func,
    brandIdState: PropTypes.string,
    brands: PropTypes.array,
    customerNumber: PropTypes.string,
    errorTankSize: PropTypes.string,
    fuelTypeIndexState: PropTypes.number,
    fuelTypesGroupState: PropTypes.array,
    generationNoState: PropTypes.number,
    generations: PropTypes.array,
    intl: PropTypes.objectOf(PropTypes.func),
    modelIdState: PropTypes.string,
    models: PropTypes.array,
    setBrandId: PropTypes.func,
    setBrands: PropTypes.func,
    setFuelTypeIndex: PropTypes.func,
    setFuelTypesGroup: PropTypes.func,
    setGenerationIdSelected: PropTypes.func,
    setGenerationObject: PropTypes.func,
    setGenerationSelected: PropTypes.func,
    setGenerations: PropTypes.func,
    setModelId: PropTypes.func,
    setModels: PropTypes.func,
    setTankSize: PropTypes.func,
    setTankSizeError: PropTypes.func,
    tankSize: PropTypes.string,
    validTankSizeField: PropTypes.func,
    validatorCallback: PropTypes.func
}

export { SelectVehicle }
